import {FC, useState} from 'react';

import {getValidOrderProductsReturnT} from '@/lib/types/rpcFunctionReturns.types';

import {usdFormatter} from '@/lib/helpers/formatters.ts';

import {useCreateDistributorOrder} from '../providers/createDistributorOrderProvider.tsx';

/**
 * Form row component props
 * @interface FormRowComponentProps
 * @property {Function} onDelete - The function to call when the row is deleted
 */
interface FormRowComponentProps {
  item: getValidOrderProductsReturnT['data'][number];
}

/**
 * Form row component for the positions form
 * @param {FormRowComponentProps} onDelete - The function to call when the row is deleted
 * @returns {JSX.Element} - The form row component
 */
const DistributorOrderFormRowComponent: FC<FormRowComponentProps> = ({item}) => {
  const {orderItems, updateOrderItem} = useCreateDistributorOrder();
  // result states
  const [error, setError] = useState<string>('');

  const handleQuantityChange = (newQuantity: number) => {
    setError('');
    if (newQuantity >= 0 && newQuantity <= item.item_count) {
      updateOrderItem(item.product.product_id, newQuantity);
    } else {
      setError('Invalid quantity');
    }
  };

  return (
    <>
      {error && (
        <tr>
          <td colSpan={10}>
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </td>
        </tr>
      )}
      <tr
        className={`border border-defaultborder dark:border-defaultborder/10 ${error ? 'outline outline-1 outline-red' : ''}`}
      >
        {/* PRODUCT */}
        <td>
          <div className="flex items-center">
            {item.product ? (
              <span className="mr-2">{item.product.product_name}</span>
            ) : (
              <span className="mr-2 text-gray-400">No product selected</span>
            )}
          </div>
        </td>
        {/* BRAND */}
        <td>
          <div className="flex items-center">
            {item.brand ? (
              <span className="mr-2">{item.brand.brand_name}</span>
            ) : (
              <span className="mr-2 text-gray-400">No brand selected</span>
            )}
          </div>
        </td>
        {/* ORDER QUANTITY */}
        <td>
          <div className="flex items-center">
            <span className="mr-2">{item.item_count}</span>
          </div>
        </td>
        {/* ORDER QUANTITY */}
        <td className="invoice-quantity-container">
          <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
            <button
              aria-label="decrease"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
              onClick={() => handleQuantityChange((orderItems[item.product.product_id] || 0) - 1)}
            >
              <i className="ri-subtract-line"></i>
            </button>
            <input
              type="text"
              className="form-control !w-12 border-0 !p-1 text-center"
              value={orderItems[item.product.product_id] || 0}
              onChange={e => handleQuantityChange(parseInt(e.target.value) || 0)}
            />
            <button
              aria-label="increase"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
              onClick={() => handleQuantityChange((orderItems[item.product.product_id] || 0) + 1)}
            >
              <i className="ri-add-line"></i>
            </button>
          </div>
        </td>

        {/* AVG RETAIL PRICE */}
        <td>
          <span className="form-control !rounded-md !p-0">
            {usdFormatter.format(item.retail_price_stats.avg_price)}
          </span>
        </td>
      </tr>
    </>
  );
};

export default DistributorOrderFormRowComponent;
